import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Excalidraw } from '@excalidraw/excalidraw';
import { ImportedDataState } from '@excalidraw/excalidraw/types/data/types';
import { ExcalidrawElement } from '@excalidraw/excalidraw/types/element/types';
import { AppState, BinaryFiles, ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types/types';
import '../whiteboard.css';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { WhiteboardSessionContext } from '../context/WhiteboardSessionContext';
import { WhiteboardContext } from '../context/WhiteboardContext';
import useThrottle from '../../../hooks/useThrottle';

const StyledAriaBox = styled('div')`
    border: 0;
    width: 1px;
    height: 1px;
    position: absolute;
    overflow: hidden;
    margin: -1px;
    padding: 0;
`;

/**
 * Wrapper for main Excalidraw component.
 */
export const ExcalidrawWrapper: React.FC<{
    initialExcalidrawData: {
        key: string,
        data: ImportedDataState
    } | undefined,
    excalidrawRefCallback: (value: ExcalidrawImperativeAPI | null) => void,
    onChange: (elements: readonly ExcalidrawElement[], appState: AppState, files: BinaryFiles) => void,
    gridModeEnabled: boolean,
    viewModeEnabled: boolean
}> = React.memo((props) => {
    const { i18n } = useTranslation();

    const { userInfo, updatePresenterPosition } = useContext(WhiteboardContext);
    const { whiteboardSessionState } = useContext(WhiteboardSessionContext);

    const { presenter, session } = whiteboardSessionState;

    const [excalidrawRef, setExcalidrawRef] = useState();
    const [ariaLiveText, setAriaLiveText] = useState('');

    const handlePointerUpdate = useCallback(({ pointer }) => {
        if (!presenter || !userInfo) {
            return;
        }

        if (presenter.userName === userInfo.userName) {
            updatePresenterPosition(session!, pointer);
        }
    }, [presenter, session, updatePresenterPosition, userInfo]);

    const { throttledFn } = useThrottle({
        callbackFn: handlePointerUpdate,
        throttleMs: 1000 / 5 // 5fps
    });

    useEffect(() => {
        if (!excalidrawRef) {
            return;
        }

        const pageTextContent = props.initialExcalidrawData?.data?.elements?.find((element) => !!element.customData?.pageTextContent)?.customData?.pageTextContent;
        if (!pageTextContent) {
            return;
        }

        const pageText = pageTextContent.items
            .map((item) => item.str.trim())
            .filter((str) => !!str)
            .join(' ');

        if (pageText) {
            setAriaLiveText(pageText);
        }
    }, [excalidrawRef, props.initialExcalidrawData?.data?.elements]);

    return (
        <>
            <StyledAriaBox role='log' aria-live='polite'>
                {ariaLiveText}
            </StyledAriaBox>

            <Excalidraw
                key={props.initialExcalidrawData?.key}
                initialData={props.initialExcalidrawData?.data}
                excalidrawAPI={(ref) => {
                    setExcalidrawRef(ref as any);
                    props.excalidrawRefCallback(ref as any);
                }}
                langCode={i18n.language}
                handleKeyboardGlobally={true}
                onPointerUpdate={throttledFn}
                {...props}
            />
        </>
    );
});
